<script lang="ts">
export default {
  name: 'DatePickerInput',
};
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-vue-next';
import { parseDate } from '@internationalized/date';
import {
  DatePickerArrow,
  DatePickerCalendar,
  DatePickerCell,
  DatePickerCellTrigger,
  DatePickerContent,
  DatePickerGrid,
  DatePickerGridBody,
  DatePickerGridHead,
  DatePickerGridRow,
  DatePickerHeadCell,
  DatePickerHeader,
  DatePickerHeading,
  DatePickerNext,
  DatePickerPrev,
  DatePickerRoot,
  DatePickerTrigger,
} from 'reka-ui';
import type { DateValue } from 'reka-ui';

const props = withDefaults(
  defineProps<{
    modelValue: string;
    label?: string;
    outlined?: boolean;
    disabled?: boolean;
    placeholder?: string;
    class?: string;
  }>(),
  {
    label: '',
    outlined: false,
    disabled: false,
    placeholder: 'Select date',
  },
);

const emit = defineEmits<{
  'update:modelValue': [value: string];
}>();

const dateValue = ref<ReturnType<typeof parseDate> | undefined>(undefined);

function parseModelValue(value: string): ReturnType<typeof parseDate> | undefined {
  if (!value || typeof value !== 'string' || value.trim() === '') return undefined;
  try {
    return parseDate(value.trim());
  } catch {
    return undefined;
  }
}

watch(
  () => props.modelValue,
  (val) => {
    dateValue.value = parseModelValue(val);
  },
  { immediate: true },
);

function dateToIsoString(date: ReturnType<typeof parseDate>): string {
  return date.toString();
}

function onDateChange(date: DateValue | undefined) {
  const d = date as ReturnType<typeof parseDate> | undefined;
  dateValue.value = d;
  emit('update:modelValue', d ? dateToIsoString(d) : '');
}

const displayText = computed(() => {
  if (!dateValue.value) return '';
  const d = dateValue.value;
  return new Date(d.year, d.month - 1, d.day).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
});

const triggerClass = computed(() => {
  const base =
    'tw:input tw:input-bordered tw:w-full tw:min-w-0 tw:flex tw:items-center tw:justify-between tw:gap-2 tw:bg-white tw:cursor-pointer tw:select-none tw:text-left';
  const disabledClass = props.disabled ? 'tw:input-disabled' : '';
  return [base, disabledClass, props.class].filter(Boolean).join(' ');
});

// Cast for reka-ui compatibility (CalendarDate from @internationalized/date is compatible at runtime)
const modelValueForPicker = computed(() => dateValue.value as DateValue | undefined);
</script>

<template>
  <div class="tw:form-control tw:w-full">
    <label
      v-if="label"
      class="tw:label">
      <span class="tw:label-text tw:text-sm">{{ label }}</span>
    </label>
    <DatePickerRoot
      :model-value="modelValueForPicker"
      :close-on-select="true"
      @update:model-value="onDateChange">
      <DatePickerTrigger
        :disabled="disabled"
        :class="triggerClass"
        type="button">
        <span :class="dateValue ? 'tw:text-gray-900' : 'tw:text-gray-500'">
          {{ displayText || placeholder }}
        </span>
        <CalendarIcon
          :size="18"
          class="tw:shrink-0 tw:text-gray-500" />
      </DatePickerTrigger>
      <Teleport to="body">
        <DatePickerContent
          :side-offset="4"
          class="tw:z-9999 tw:rounded-xl tw:border tw:border-gray-200 tw:bg-white tw:shadow-lg tw:will-change-[transform,opacity]">
          <DatePickerArrow class="tw:fill-white" />
          <DatePickerCalendar
            v-slot="{ weekDays, grid }"
            class="tw:p-4">
            <DatePickerHeader class="tw:flex tw:items-center tw:justify-between">
              <DatePickerPrev
                class="tw:inline-flex tw:h-8 tw:w-8 tw:cursor-pointer tw:items-center tw:justify-center tw:rounded-md tw:bg-transparent tw:text-gray-500 tw:transition-colors tw:hover:bg-gray-100">
                <ChevronLeftIcon :size="17" />
              </DatePickerPrev>
              <DatePickerHeading class="tw:font-medium tw:text-gray-900" />
              <DatePickerNext
                class="tw:inline-flex tw:h-8 tw:w-8 tw:cursor-pointer tw:items-center tw:justify-center tw:rounded-md tw:bg-transparent tw:text-gray-500 tw:transition-colors tw:hover:bg-gray-100">
                <ChevronRightIcon :size="17" />
              </DatePickerNext>
            </DatePickerHeader>
            <div class="tw:flex tw:flex-col tw:space-y-4 tw:pt-4">
              <DatePickerGrid
                v-for="month in grid"
                :key="month.value.toString()"
                class="tw:w-full tw:border-collapse tw:space-y-1 tw:select-none">
                <DatePickerGridHead>
                  <DatePickerGridRow class="tw:mb-1 tw:flex tw:w-full tw:justify-between">
                    <DatePickerHeadCell
                      v-for="day in weekDays"
                      :key="day"
                      class="tw:w-8 tw:rounded-md tw:text-xs tw:font-medium tw:text-gray-600">
                      {{ day }}
                    </DatePickerHeadCell>
                  </DatePickerGridRow>
                </DatePickerGridHead>
                <DatePickerGridBody>
                  <DatePickerGridRow
                    v-for="(weekDates, index) in month.rows"
                    :key="`weekDate-${index}`"
                    class="tw:flex tw:w-full">
                    <DatePickerCell
                      v-for="weekDate in weekDates"
                      :key="weekDate.toString()"
                      :date="weekDate">
                      <DatePickerCellTrigger
                        :day="weekDate"
                        :month="month.value"
                        class="tw:data-[selected]:tw:bg-primary tw:data-[selected]:tw:text-white tw:data-[selected]:tw:border-transparent tw:data-[today]:tw:ring tw:data-[today]:tw:ring-primary/50 tw:data-[disabled]:tw:opacity-40 tw:data-[unavailable]:tw:line-through tw:data-[unavailable]:tw:text-gray-400 tw:relative tw:flex tw:h-8 tw:w-8 tw:items-center tw:justify-center tw:rounded-md tw:border tw:border-transparent tw:bg-transparent tw:text-sm tw:text-gray-900 tw:outline-none tw:hover:border-gray-300 tw:hover:bg-gray-50" />
                    </DatePickerCell>
                  </DatePickerGridRow>
                </DatePickerGridBody>
              </DatePickerGrid>
            </div>
          </DatePickerCalendar>
        </DatePickerContent>
      </Teleport>
    </DatePickerRoot>
  </div>
</template>
